import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowBendUpRight, ArrowClockwise, CaretLeft, CaretRight, Check, Info, Table, Tray,
  Warning, X,
} from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode, getTickets as fetchTickets,
  getAgentCodes, getEmployeeCodes, getMinimumOnePercentSubscriberDetails,
  updateAgentToDirect, updateAgentToAgent, updateAgentToSubId, saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

const PAGE_SIZE = 10;

// The API hands dates back as "31-08-2021 00:00:00". Every date in this report is
// date-only, so the midnight time component is dropped rather than shown as noise.
function formatDate(value) {
  if (!value) return '—';
  return String(value).trim().replace(/\s+00:00:00$/, '') || '—';
}

// Updateagenttodirect / Updateagenttosubid interpolate transaction_date straight into a
// fn_agentcommission call, so it has to be handed over as yyyy-MM-dd whatever
// shape the API returned it in (it sends day-first date-times on this endpoint
// family). Anything unparseable falls back to today rather than reaching SQL
// half-formed.
function toSqlDate(value) {
  const text = String(value || '').trim();
  const yearFirst = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (yearFirst) return `${yearFirst[1]}-${yearFirst[2]}-${yearFirst[3]}`;
  const dayFirst = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dayFirst) {
    let [, day, month] = dayFirst;
    const year = dayFirst[3];
    if (Number(month) > 12 && Number(day) <= 12) [day, month] = [month, day];
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPlain(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

const TH_BASE = { padding: '10px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280', whiteSpace: 'nowrap' };
const TD_BASE = { padding: '10px 12px', whiteSpace: 'nowrap' };

// Columns come straight from GetMinimumOnePercentSubscriberDetails, regrouped so
// the identifying fields (account / subscriber / agent) read before the money
// ones. Ticket No, Joined Branch and Parent ID are deliberately not shown: the
// ticket is fixed by the selector above and repeated in the confirmation text,
// and the other two are not part of the decision this grid supports.
const COLUMNS = [
  { key: 'account_name', label: 'Old Account Name', render: (r) => formatPlain(r.account_name), mono: true },
  { key: 'subscriber_name', label: 'Old Subscriber Name', render: (r) => formatPlain(r.subscriber_name) },
  { key: 'referral_code', label: 'Old Referral Code', render: (r) => formatPlain(r.referral_code), mono: true },
  { key: 'enroll_date', label: 'Enroll Date', render: (r) => formatDate(r.enroll_date) },
  { key: 'commission_date', label: 'Commission Date', render: (r) => formatDate(r.commission_date) },
  { key: 'commission_transaction_number', label: 'Commission Txn No', render: (r) => formatPlain(r.commission_transaction_number), mono: true },
  { key: 'commission_amount', label: 'Commission Amount', render: (r) => formatAmount(r.commission_amount), numeric: true },
  { key: 'tds_amount', label: 'TDS Amount', render: (r) => formatAmount(r.tds_amount), numeric: true },
  { key: 'account_balance', label: 'Account Balance', render: (r) => formatAmount(r.account_balance), numeric: true },
  { key: 'total_chit_value', label: 'Total Chit Value', render: (r) => formatAmount(r.total_chit_value), numeric: true },
  { key: 'minimum_1_percent', label: 'Minimum 1%', render: (r) => formatAmount(r.minimum_1_percent), numeric: true },
];

const MODES = [
  {
    id: 'direct',
    letter: 'B',
    title: 'Agent To Direct',
    subtitle: 'Agent commission entries where the account balance is within the minimum 1% of the chit value',
    action: 'converted to',
    targetLabel: 'DIRECT',
    wired: true,
    confirmCta: 'Yes, Convert To Direct',
    confirmIntro: 'You’re about to re-point this subscriber’s introducer to DIRECT and delete the agent commission records behind it. Please review before confirming.',
    tickWarning: (ticketNo) => `This deletes the commission, TDS voucher and transaction records for ticket ${ticketNo} and cannot be undone from this screen.`,
    failureMessage: 'Could not convert this subscriber to DIRECT. Please try again.',
  },
  {
    id: 'agent',
    letter: 'C',
    title: 'Agent To Agent',
    subtitle: 'Re-point the same commission entries from the current agent code to another one',
    action: 're-pointed to',
    targetLabel: null,
    wired: true,
    confirmCta: 'Yes, Change Agent Code',
    confirmIntro: 'You’re about to re-point this subscriber’s introducer to the new agent code and clear the commission paperwork behind the old one. Please review before confirming.',
    tickWarning: (ticketNo) => `This deletes the TDS voucher and transaction records for ticket ${ticketNo} and re-points its commission row to the new agent. It cannot be undone from this screen.`,
    failureMessage: 'Could not change the agent code for this subscriber. Please try again.',
  },
  {
    id: 'subid',
    letter: 'D',
    title: 'Agent To SubID',
    subtitle: 'Keep the current agent and record an employee as the sub introducer',
    action: 're-pointed to sub ID',
    // Resolved from the New Employee Code dropdown, not fixed like Agent To Direct.
    targetLabel: null,
    wired: true,
    confirmCta: 'Yes, Change To Sub ID',
    confirmIntro: 'You’re about to record an employee as this subscriber’s sub introducer, keeping the current agent, and rebuild the commission behind it. Please review before confirming.',
    tickWarning: (ticketNo) => `This deletes the commission, TDS voucher and transaction records for ticket ${ticketNo} and regenerates the commission. It cannot be undone from this screen.`,
    failureMessage: 'Could not change this subscriber to a sub ID. Please try again.',
  },
];

// Ticks and page positions are per mode, so paging or ticking one grid leaves
// the other two exactly as the operator left them.
const EMPTY_ACK = { direct: false, agent: false, subid: false };
// Each conversion carries its own Reason, recorded in its own SaveData audit entry.
const EMPTY_REASONS = { direct: '', agent: '', subid: '' };
const reasonError = (text) => text.trim().length < 10 || text.length > 200;
const EMPTY_PAGES = { direct: 0, agent: 0, subid: 0 };

function SummaryTile({ label, value, accent }) {
  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', background: '#F9FAFB' }}>
      <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: '700', color: accent || '#111827' }}>{value}</div>
    </div>
  );
}

// One conversion block: section heading, any inputs the mode needs, the summary
// tiles, the commission grid, the acknowledgement tick and the Save that tick
// gates. `gateMessage` stands in for the grid when the mode is not ready to show
// one yet — Agent To Agent uses it until an agent code has been picked.
function ConversionGrid({
  mode, rows, totals, first, ticketNo, columns, targetLabel, controls, gateMessage,
  page, onPageChange, acknowledged, onAcknowledgedChange, onSave, onClear, savingMode, loading,
  reason, onReasonChange, reasonTouched,
}) {
  const prefix = `agent-code-change-${mode.id}`;
  // Any in-flight save locks every grid: they all write against the same rows.
  const busy = savingMode !== null;
  const saving = savingMode === mode.id;

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const showPaginator = rows.length > PAGE_SIZE;

  return (
    <div data-testid={`${prefix}-section`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {mode.letter}
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{mode.title}</div>
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{mode.subtitle}</div>
        </div>
      </div>

      {controls}

      {gateMessage ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '30px', color: '#9CA3AF' }} data-testid={`${prefix}-gate`}>
          <Table size={28} />
          <div style={{ fontSize: '13px' }}>{gateMessage}</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '12px' }} data-testid={`${prefix}-summary`}>
            <SummaryTile label="Records" value={rows.length} />
            <SummaryTile label="Total Commission" value={formatAmount(totals.commission)} accent="#059669" />
            <SummaryTile label="Total TDS" value={formatAmount(totals.tds)} accent="#D97706" />
            <SummaryTile label="Minimum 1%" value={formatAmount(first?.minimum_1_percent)} />
            <SummaryTile
              label="Account Balance"
              value={formatAmount(first?.account_balance)}
              accent={Number(first?.account_balance ?? 0) < 0 ? '#DC2626' : undefined}
            />
          </div>

          {/* width:0 + minWidth:100% keeps this screen's wide table from widening the
              workspace column it sits in. That column is a `1fr` grid track, which
              defaults to min-width:auto and would otherwise stretch to the table's full
              width, pushing the checkbox and action bar off-screen. A definite width of
              zero makes the intrinsic contribution zero, while min-width:100% still fills
              the column — so the table scrolls inside this box instead of expanding it.
              Scoped here rather than on the shared workspace page. */}
          <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px', width: 0, minWidth: '100%' }}>
            <table style={{ width: '100%', minWidth: `${columns.length * 100}px`, borderCollapse: 'collapse', fontSize: '13px' }} data-testid={`${prefix}-table`}>
              <thead>
                <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  {columns.map((col) => (
                    <th key={col.key} style={{ ...TH_BASE, textAlign: col.numeric ? 'right' : 'left' }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row, i) => (
                  <tr
                    key={`${row.commission_transaction_number || 'row'}-${safePage * PAGE_SIZE + i}`}
                    style={{ borderBottom: '1px solid #F3F4F6' }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          ...TD_BASE,
                          textAlign: col.numeric ? 'right' : 'left',
                          fontFamily: col.mono ? 'monospace' : undefined,
                        }}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {showPaginator && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', padding: '10px 12px', borderTop: '1px solid #E5E7EB', background: '#F9FAFB' }}>
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(0, safePage - 1))}
                  disabled={safePage === 0}
                  className="btn-secondary"
                  style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', opacity: safePage === 0 ? 0.5 : 1 }}
                  data-testid={`${prefix}-page-prev`}
                >
                  <CaretLeft size={12} />
                </button>
                <span style={{ fontSize: '11px', color: '#6B7280' }}>
                  Page {safePage + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => onPageChange(Math.min(totalPages - 1, safePage + 1))}
                  disabled={safePage >= totalPages - 1}
                  className="btn-secondary"
                  style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', opacity: safePage >= totalPages - 1 ? 0.5 : 1 }}
                  data-testid={`${prefix}-page-next`}
                >
                  <CaretRight size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Gate for the destructive save — sits directly under the grid it applies to;
              the Reason and Save follow it. The button is not a child of the label so
              that clicking it does not toggle the tick. */}
          <div style={{ marginTop: '14px' }}>
            <label
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '12px 14px', borderRadius: '8px', cursor: busy ? 'default' : 'pointer',
                border: `1px solid ${acknowledged ? '#FCA5A5' : '#E5E7EB'}`,
                background: acknowledged ? '#FEF2F2' : '#F9FAFB',
              }}
              data-testid={`${prefix}-confirm-checkbox-label`}
            >
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={busy}
                onChange={(e) => onAcknowledgedChange(e.target.checked)}
                style={{ marginTop: '2px', width: '15px', height: '15px', accentColor: '#DC2626', cursor: busy ? 'default' : 'pointer' }}
                data-testid={`${prefix}-confirm-checkbox`}
              />
              <span style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
                I confirm these {rows.length} agent commission {rows.length === 1 ? 'entry' : 'entries'} should be
                {' '}{mode.action} <strong>{targetLabel}</strong>.
                <span style={{ display: 'block', color: '#991B1B', marginTop: '2px' }}>
                  {mode.tickWarning(ticketNo)}
                </span>
              </span>
            </label>
          </div>

          {/* Reason for this conversion — last field, recorded in its audit log entry. */}
          <div style={{ marginTop: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
              <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
                {reason.length} / 200
              </span>
            </div>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonTouched && reasonError(reason) ? '#DC2626' : undefined }}
              rows={3}
              maxLength={200}
              placeholder={`Explain why this ${mode.title} change is being made...`}
              value={reason}
              disabled={busy}
              onChange={(e) => onReasonChange(e.target.value)}
              data-testid={`${prefix}-reason`}
            />
            {reasonTouched && reasonError(reason) && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Minimum 10 characters required</div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
            <div style={{ display: 'flex', gap: '12px', alignSelf: 'center' }}>
              <button
                type="button"
                onClick={onClear}
                className="btn-secondary"
                style={{ whiteSpace: 'nowrap' }}
                disabled={loading || busy}
                data-testid={`${prefix}-clear-button`}
              >
                <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
              </button>
              {/* Deliberately not disabled when the box is unticked — clicking it then is
                  what surfaces the "tick the box first" message. */}
              <button
                type="button"
                onClick={() => onSave(mode)}
                className="btn-primary"
                style={{ whiteSpace: 'nowrap' }}
                disabled={loading || busy}
                data-testid={`${prefix}-save-button`}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AgentCodeChange() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);
  const [agentCodes, setAgentCodes] = useState([]);
  const [employeeCodes, setEmployeeCodes] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);
  const [ticketNo, setTicketNo] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pages, setPages] = useState(EMPTY_PAGES);
  const { toast, setToast, showToast } = useToast();

  // Agent To Agent only: the code the subscriber is being moved to. Nothing in
  // that section's grid renders until this is picked.
  const [newReferralCode, setNewReferralCode] = useState('');
  // Agent To SubID only: the employee recorded as the sub introducer.
  const [newEmployeeCode, setNewEmployeeCode] = useState('');

  // Which conversion is open. Null until the operator picks one, so a loaded
  // ticket shows the three choices rather than three stacked grids.
  const [activeMode, setActiveMode] = useState(null);

  // Each save is destructive, so it is gated on an explicit tick rather than on
  // the button alone. Every tick resets whenever the loaded rows change.
  const [acknowledged, setAcknowledged] = useState(EMPTY_ACK);
  const [reasons, setReasons] = useState(EMPTY_REASONS);
  const [reasonTouched, setReasonTouched] = useState(EMPTY_ACK);
  // Which mode is mid-save (null when idle) and which one the modal is confirming.
  const [savingMode, setSavingMode] = useState(null);
  const [confirmMode, setConfirmMode] = useState(null);

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // The agent list is branch-independent, so it is fetched once alongside the
    // branches rather than reloaded for every ticket.
    getAgentCodes().then((res) => setAgentCodes(res || [])).catch(() => setAgentCodes([]));
    getEmployeeCodes().then((res) => setEmployeeCodes(res || [])).catch(() => setEmployeeCodes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetGrid = () => {
    setRows([]);
    setLoaded(false);
    setPages(EMPTY_PAGES);
    // Never carry a tick — or an agent picked against different rows — across a
    // different set of rows.
    setAcknowledged(EMPTY_ACK);
    setReasons(EMPTY_REASONS);
    setReasonTouched(EMPTY_ACK);
    setNewReferralCode('');
    setNewEmployeeCode('');
    setActiveMode(null);
    setConfirmMode(null);
  };

  const resetFromTicket = () => {
    setTicketNo(null);
    resetGrid();
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
  };

  const loadRows = async (schema, gCode, tNo, bCode) => {
    setLoading(true);
    try {
      const res = await getMinimumOnePercentSubscriberDetails(schema, gCode, tNo, bCode);
      setRows(Array.isArray(res) ? res : []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      setRows([]);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Could not load subscriber details for this ticket.'));
    } finally {
      setLoaded(true);
      setPages(EMPTY_PAGES);
      setLoading(false);
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (!selected) return;
    // Branch code doubles as the branch schema name, the same way every other
    // Supporting Tools screen resolves it.
    setBranchschema(selected.branch_code);
    setBranchCode(selected.branch_code);
    fetchGroupCode(selected.branch_code)
      .then((res) => setBranchCodeList(res || []))
      .catch(() => setBranchCodeList([]));
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (!selected) return;
    setGroupCode(selected.group_Code);
    // GetGroupcodes returns chitgroup_id as a string, and '' when the column is
    // NULL — the save validates it before binding it to a non-nullable int.
    setChitgroupId(selected.chitgroup_id);
    fetchTickets(branchschema, selected.group_Code)
      .then((res) => setTickets(res || []))
      .catch(() => setTickets([]));
  };

  const handleTicketChange = (value) => {
    setTicket(value || '');
    resetFromTicket();
    if (!value) return;
    const selected = tickets.find((x) => String(x.ticketno) === String(value));
    if (!selected) return;
    setTicketNo(selected.ticketno);
    loadRows(branchschema, groupCode, selected.ticketno, branchCode);
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
  };

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    commission: acc.commission + Number(r.commission_amount || 0),
    tds: acc.tds + Number(r.tds_amount || 0),
  }), { commission: 0, tds: 0 }), [rows]);

  const first = rows[0];
  const oldReferralCode = first?.referral_code || '';

  const transactionNumbers = useMemo(
    () => rows.map((r) => r.commission_transaction_number).filter(Boolean),
    [rows],
  );
  const transactionNoList = transactionNumbers.join("','");

  // Moving a subscriber onto the code it already sits on is a no-op, so the
  // current code is dropped from the choices rather than left there to be picked.
  const agentCodeOptions = useMemo(
    () => (agentCodes || []).filter((a) => a.referralCode && a.referralCode !== oldReferralCode),
    [agentCodes, oldReferralCode],
  );
  const newAgent = useMemo(
    () => agentCodeOptions.find((a) => a.referralCode === newReferralCode) || null,
    [agentCodeOptions, newReferralCode],
  );

  // Agent To SubID keeps the current agent as the introducer, so its contact id
  // is read off the full agent list (which still contains the old code that
  // agentCodeOptions drops).
  const oldAgent = useMemo(
    () => (agentCodes || []).find((a) => a.referralCode === oldReferralCode) || null,
    [agentCodes, oldReferralCode],
  );
  const newEmployee = useMemo(
    () => (employeeCodes || []).find((e) => e.employee_code === newEmployeeCode) || null,
    [employeeCodes, newEmployeeCode],
  );

  // Agent To Agent and Agent To SubID both show what the ticket is moving to
  // right beside the code it is moving from — the referral code for one, the
  // employee code for the other. Agent To Direct has no such column: its target
  // is the fixed DIRECT.
  const columnsByMode = useMemo(() => {
    const withTarget = (column) => COLUMNS.flatMap((col) => (
      col.key === 'referral_code' ? [col, column] : [col]
    ));
    return {
      direct: COLUMNS,
      agent: withTarget({
        key: 'new_referral_code',
        label: 'New Referral Code',
        render: () => formatPlain(newReferralCode),
        mono: true,
      }),
      subid: withTarget({
        key: 'new_employee_code',
        label: 'New Employee Code',
        render: () => formatPlain(newEmployeeCode),
        mono: true,
      }),
    };
  }, [newReferralCode, newEmployeeCode]);

  const targetLabelFor = (mode) => {
    if (mode.id === 'agent') return newReferralCode || 'the selected agent';
    if (mode.id === 'subid') return newEmployeeCode || 'the selected employee';
    return mode.targetLabel;
  };

  // Shared by all three Save buttons; `mode` says which grid was saved from.
  const handleSave = (mode) => {
    if (!rows.length) {
      showToast('warn', 'Nothing To Save', 'Load a ticket that has subscriber details before saving.');
      return;
    }

    setReasonTouched((prev) => ({ ...prev, [mode.id]: true }));
    if (reasonError(reasons[mode.id] || '')) {
      showToast('warn', 'Reason Required', `Enter a reason for the ${mode.title} change (10 to 200 characters).`);
      return;
    }

    // The tick is the gate the operator has to pass before anything is written.
    if (!acknowledged[mode.id]) {
      showToast(
        'warn',
        'Confirmation Required',
        `Tick the confirmation box below the ${mode.title} grid before saving — this change deletes commission entries and cannot be undone from this screen.`,
      );
      return;
    }

    // Checked before the payload validations below, which would only confuse an
    // operator on a grid whose conversion cannot run at all yet.
    if (!mode.wired) {
      showToast(
        'warn',
        'Not Available Yet',
        `The ${mode.title} conversion has no API behind it yet, so nothing was saved.`,
      );
      return;
    }

    if (!Number.isFinite(Number(chitgroupId)) || Number(chitgroupId) <= 0) {
      showToast('warn', 'No Data Found', 'No chit group id found for the selected group. Please re-select the group.');
      return;
    }

    if (first?.contact_id == null) {
      showToast('warn', 'Incomplete Form', 'Could not resolve the subscriber contact for this ticket. Please select the ticket again.');
      return;
    }

    if (mode.id === 'direct' && first?.commission_contact_id == null) {
      showToast('warn', 'Incomplete Form', 'Could not resolve the agent contact for this ticket. Please select the ticket again.');
      return;
    }

    if (!transactionNumbers.length) {
      showToast('warn', 'Incomplete Form', 'No commission transaction number found on these rows. Please select the ticket again.');
      return;
    }

    if (mode.id === 'agent') {
      if (!newReferralCode) {
        showToast('warn', 'Confirmation Required', 'Select a New Referral Code before saving.');
        return;
      }
      // Updateagenttoagent writes this straight into introduced_id, so a code the
      // list could not resolve an id for must not reach the request.
      if (!(Number(newAgent?.contactId) > 0)) {
        showToast('warn', 'Incomplete Form', `Could not resolve the contact id for agent code ${newReferralCode}. Please re-select it.`);
        return;
      }
    }

    if (mode.id === 'subid') {
      if (!newEmployeeCode) {
        showToast('warn', 'Confirmation Required', 'Select a New Employee Code before saving.');
        return;
      }
      // Updateagenttosubid writes this into subintroduced_id.
      if (!(Number(newEmployee?.contact_id) > 0)) {
        showToast('warn', 'Incomplete Form', `Could not resolve the contact id for employee code ${newEmployeeCode}. Please re-select it.`);
        return;
      }
      // ...and the current agent stays the introducer, so that id is needed too.
      if (!(Number(oldAgent?.contactId) > 0)) {
        showToast('warn', 'Incomplete Form', `Could not resolve the contact id for the current agent code ${oldReferralCode || '—'}. Please select the ticket again.`);
        return;
      }
    }

    setConfirmMode(mode.id);
  };

  const cancelConfirm = () => {
    if (savingMode !== null) return;
    setConfirmMode(null);
  };

  // Everything the confirmed save needs, resolved per mode so the request, the
  // audit row and the success wording differ while the flow around them stays
  // shared. Only the wired modes get here — handleSave stops the rest.
  const buildSave = (mode) => {
    const auditHeader = {
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
    };
    // The operator's own reason, as on every other screen.
    const typedReason = (reasons[mode.id] || '').trim();
    const oldData = {
      branchCode,
      groupCode,
      chitgroupId,
      ticketNo,
      subscriberName: first.subscriber_name,
      referralCode: first.referral_code,
      contactId: first.contact_id,
      commissionContactId: first.commission_contact_id,
      commissionTransactionNumbers: transactionNumbers,
      commissionAmount: totals.commission,
      tdsAmount: totals.tds,
      accountBalance: first.account_balance,
      minimum1Percent: first.minimum_1_percent,
      recordCount: rows.length,
    };

    if (mode.id === 'agent') {
      return {
        request: () => updateAgentToAgent({
          branchschema,
          // The interbranch update targets the same schema as the branch updates.
          interBranchSchema: branchschema,
          chitgroupId,
          ticketno: ticketNo,
          contactId: first.contact_id,
          introducedId: newAgent.contactId,
          // The commenced enrolment sits under the group / ticket the grid was
          // loaded for. The endpoint takes these separately for tickets commenced
          // under another group, which this screen has no way to select.
          commencedChitgroupId: chitgroupId,
          commencedTicketno: ticketNo,
          paymentVoucherNumber: transactionNoList,
          commissionVoucherNo: transactionNoList,
          // Matched with `=` server-side rather than `in(...)`, so this one takes
          // a single value where the others take the whole list.
          commissionTransactionNumber: first.commission_transaction_number,
          transactionNo: transactionNoList,
        }),
        audit: {
          ...auditHeader,
          changeType: 'AGENT_TO_AGENT_SUBSCRIBER_INTRODUCER_UPDATE',
          oldData,
          newData: { referralCode: newReferralCode, introducedId: newAgent.contactId },
          reason: typedReason,
        },
        success: `Agent code changed to ${newReferralCode} and audit saved successfully.`,
      };
    }

    if (mode.id === 'subid') {
      return {
        request: () => updateAgentToSubId({
          branchschema,
          // The interbranch update targets the same schema as the branch updates.
          interBranchSchema: branchschema,
          chitgroupId,
          ticketno: ticketNo,
          contactId: first.contact_id,
          // The agent is kept; the employee is recorded as the sub introducer.
          introducedId: oldAgent.contactId,
          subintroducedId: newEmployee.contact_id,
          paymentVoucherNumber: transactionNoList,
          transactionNo: transactionNoList,
          // Handed to fn_agentcommission when it rebuilds the commission row; the
          // transaction date is the subscriber's enroll date.
          transactionDate: toSqlDate(first.enroll_date),
          groupcode: groupCode,
        }),
        audit: {
          ...auditHeader,
          changeType: 'AGENT_TO_SUBID_SUBSCRIBER_INTRODUCER_UPDATE',
          oldData,
          newData: {
            referralCode: oldReferralCode,
            employeeCode: newEmployeeCode,
            introducedId: oldAgent.contactId,
            subintroducedId: newEmployee.contact_id,
          },
          reason: typedReason,
        },
        success: `Sub ID set to ${newEmployeeCode} and audit saved successfully.`,
      };
    }

    return {
      request: () => updateAgentToDirect({
        branchSchema: branchschema,
        // The interbranch update targets the same schema as the branch updates.
        interBranchSchema: branchschema,
        chitgroupId,
        ticketno: ticketNo,
        contactId: first.contact_id,
        commissionContactId: first.commission_contact_id,
        paymentVoucherNumber: transactionNoList,
        transactionNo: transactionNoList,
        // Handed to fn_agentcommission when it rebuilds the commission row; the
        // transaction date is the subscriber's enroll date.
        transactionDate: toSqlDate(first.enroll_date),
        groupcode: groupCode,
      }),
      audit: {
        ...auditHeader,
        changeType: 'AGENT_TO_DIRECT_SUBSCRIBER_INTRODUCER_UPDATE',
        oldData,
        newData: { referralCode: 'DIRECT', introducedId: 1, subintroducedId: 1 },
        reason: typedReason,
      },
      success: 'Subscriber converted to DIRECT and audit saved successfully.',
    };
  };

  // Fires only after the operator confirms in the modal.
  const confirmSave = async () => {
    const mode = MODES.find((m) => m.id === confirmMode);
    if (!mode) return;

    const { request, audit, success } = buildSave(mode);
    setSavingMode(mode.id);

    try {
      const res = await request();
      // All three Updateagentto* actions return Ok(string); a { message } shape is
      // still read in case the controller wraps it later.
      const apiMessage = typeof res === 'string' ? res : res?.message;

      try {
        await saveData([audit]);
        setSavingMode(null);
        setConfirmMode(null);
        showToast('success', 'Success', apiMessage || success, true);
      } catch (auditErr) {
        setSavingMode(null);
        setConfirmMode(null);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Record updated, but audit log failed to save.', true);
      }

      // The rows that were just changed must not stay on screen as if untouched,
      // and the agent picked against them no longer applies to what reloads.
      setAcknowledged(EMPTY_ACK);
      setReasons(EMPTY_REASONS);
      setReasonTouched(EMPTY_ACK);
      setNewReferralCode('');
      setNewEmployeeCode('');
      loadRows(branchschema, groupCode, ticketNo, branchCode);
    } catch (err) {
      setSavingMode(null);
      setConfirmMode(null);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, mode.failureMessage), true);
    }
  };

  const hasRows = Boolean(ticket) && !loading && rows.length > 0;
  const activeModeConfig = MODES.find((m) => m.id === activeMode) || null;
  const confirming = MODES.find((m) => m.id === confirmMode) || null;

  // Agent To Agent's own inputs: the code being moved away from (read straight off
  // the loaded rows) and the code being moved to.
  const agentControls = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
          Old Referral Code
        </label>
        <input
          className="input-field"
          style={{ fontSize: '13px', background: '#F9FAFB', fontFamily: 'monospace' }}
          value={oldReferralCode}
          readOnly
          placeholder="—"
          data-testid="agent-code-change-agent-old-referral"
        />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
          New Referral Code <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <SearchableSelect
          key={`new-referral-${formKey}-${ticketNo ?? 'none'}`}
          options={agentCodeOptions}
          getLabel={(o) => o.referralCode}
          getValue={(o) => o.referralCode}
          value={newReferralCode}
          onChange={(value) => {
            setNewReferralCode(value || '');
            // A different target means the tick no longer covers what would be saved.
            setAcknowledged((prev) => ({ ...prev, agent: false }));
            setPages((prev) => ({ ...prev, agent: 0 }));
          }}
          placeholder="Select new referral code"
          emptyMessage="No agent codes found"
          disabled={savingMode !== null}
          testId="agent-code-change-agent-new-referral-select"
        />
      </div>
    </div>
  );

  // Agent To SubID mirrors Agent To Agent, except the second field picks the
  // employee who becomes the sub introducer rather than a replacement agent —
  // the agent in the first field is kept.
  const subidControls = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
          Old Referral Code
        </label>
        <input
          className="input-field"
          style={{ fontSize: '13px', background: '#F9FAFB', fontFamily: 'monospace' }}
          value={oldReferralCode}
          readOnly
          placeholder="—"
          data-testid="agent-code-change-subid-old-referral"
        />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
          New Employee Code <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <SearchableSelect
          key={`new-employee-${formKey}-${ticketNo ?? 'none'}`}
          options={employeeCodes}
          getLabel={(o) => o.employee_code}
          getValue={(o) => o.employee_code}
          value={newEmployeeCode}
          onChange={(value) => {
            setNewEmployeeCode(value || '');
            // A different sub ID means the tick no longer covers what would be saved.
            setAcknowledged((prev) => ({ ...prev, subid: false }));
            setPages((prev) => ({ ...prev, subid: 0 }));
          }}
          placeholder="Select new employee code"
          emptyMessage="No employee codes found"
          disabled={savingMode !== null}
          testId="agent-code-change-subid-new-employee-select"
        />
      </div>
    </div>
  );

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="agent-code-change-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="agent-code-change-form">
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowBendUpRight size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Agent Allocation Delete</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load agent commission details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Branch (CAO) <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              testId="agent-code-change-cao-select"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!cao}
              testId="agent-code-change-group-select"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group first"
              disabled={!group}
              testId="agent-code-change-ticket-select"
            />
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* The load states are shared by all three grids, so they are shown once
            here instead of being repeated inside every section. */}
        {!ticket && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '30px', color: '#9CA3AF' }} data-testid="agent-code-change-idle">
            <Table size={28} />
            <div style={{ fontSize: '13px' }}>Select a branch, group and ticket to load the grids.</div>
          </div>
        )}

        {ticket && loading && (
          <div style={{ padding: '30px', textAlign: 'center', fontSize: '13px', color: '#9CA3AF' }} data-testid="agent-code-change-loading">
            Loading subscriber details...
          </div>
        )}

        {ticket && !loading && loaded && rows.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '30px', color: '#9CA3AF' }} data-testid="agent-code-change-empty">
            <Tray size={28} />
            <div style={{ fontSize: '13px' }}>No subscriber details found for the selected ticket.</div>
          </div>
        )}

        {/* SECTION B: pick the conversion. Only the chosen one renders below, so
            the operator reads one grid rather than three stacked copies of the
            same rows. */}
        {hasRows && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Choose Conversion</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Pick one to see the entries it would change</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '20px' }} data-testid="agent-code-change-mode-picker">
              {MODES.map((mode) => {
                const open = activeMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setActiveMode(open ? null : mode.id)}
                    disabled={savingMode !== null}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px',
                      borderRadius: '8px', textAlign: 'left',
                      cursor: savingMode !== null ? 'not-allowed' : 'pointer',
                      border: `1px solid ${open ? '#002FA7' : '#E5E7EB'}`,
                      background: open ? '#EFF6FF' : 'white',
                    }}
                    data-testid={`agent-code-change-mode-${mode.id}`}
                  >
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, background: open ? '#002FA7' : '#E5E7EB', color: open ? 'white' : '#6B7280', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {mode.letter}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{mode.title}</div>
                      <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{mode.subtitle}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {!activeModeConfig && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '30px', color: '#9CA3AF' }} data-testid="agent-code-change-no-mode">
                <Table size={28} />
                <div style={{ fontSize: '13px' }}>Choose one of the three conversions above to see its details.</div>
              </div>
            )}

            {activeModeConfig && (
              <ConversionGrid
                mode={activeModeConfig}
                rows={rows}
                totals={totals}
                first={first}
                ticketNo={ticketNo}
                columns={columnsByMode[activeModeConfig.id]}
                targetLabel={targetLabelFor(activeModeConfig)}
                controls={{
                  agent: agentControls,
                  subid: subidControls,
                }[activeModeConfig.id] || null}
                gateMessage={(() => {
                  if (activeModeConfig.id === 'agent' && !newReferralCode) {
                    return 'Select a New Referral Code to load the entries this change will move.';
                  }
                  if (activeModeConfig.id === 'subid' && !newEmployeeCode) {
                    return 'Select a New Employee Code to load the entries this change will move.';
                  }
                  return null;
                })()}
                page={pages[activeModeConfig.id]}
                onPageChange={(p) => setPages((prev) => ({ ...prev, [activeModeConfig.id]: p }))}
                acknowledged={acknowledged[activeModeConfig.id]}
                onAcknowledgedChange={(checked) => setAcknowledged((prev) => ({ ...prev, [activeModeConfig.id]: checked }))}
                reason={reasons[activeModeConfig.id]}
                onReasonChange={(text) => setReasons((prev) => ({ ...prev, [activeModeConfig.id]: text }))}
                reasonTouched={reasonTouched[activeModeConfig.id]}
                onSave={handleSave}
                onClear={handleClear}
                savingMode={savingMode}
                loading={loading}
              />
            )}
          </>
        )}

        {/* Clear and Save Changes both sit under the open conversion grid. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF', marginTop: '24px' }}>
          <Info size={13} /> Each conversion saves on its own — tick the box under its grid, then use Save Changes
        </div>
      </div>

      {/* Confirm modal — shared by every wired conversion, worded from its mode. */}
      {confirming && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '480px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="agent-code-change-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Warning size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm {confirming.title}</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>{confirming.confirmIntro}</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Group / Ticket</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{groupCode} / {ticketNo}</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Subscriber</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{first?.subscriber_name || '—'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Referral Code</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>
                    {oldReferralCode || '—'} <span style={{ color: '#9CA3AF' }}>→</span>{' '}
                    <span style={{ color: '#059669', fontWeight: '600' }}>{targetLabelFor(confirming)}</span>
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Records Affected</p>
                  <p style={{ fontSize: '13px', color: '#DC2626', fontWeight: '600', margin: 0 }}>{rows.length}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#991B1B', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
                <Warning size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                {confirming.id === 'agent' ? (
                  <span>
                    TDS voucher and total-transaction rows for transaction{' '}
                    {transactionNumbers.length === 1 ? 'number' : 'numbers'}{' '}
                    <strong>{transactionNumbers.join(', ')}</strong> will be permanently deleted, and commission
                    transaction <strong>{formatPlain(first?.commission_transaction_number)}</strong> will be
                    re-pointed to <strong>{newReferralCode}</strong>. This cannot be undone from this screen.
                  </span>
                ) : (
                  <span>
                    Commission, TDS voucher and total-transaction rows for transaction{' '}
                    {transactionNumbers.length === 1 ? 'number' : 'numbers'}{' '}
                    <strong>{transactionNumbers.join(', ')}</strong> will be permanently deleted. This cannot be undone from this screen.
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px', marginTop: '10px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{(reasons[confirming.id] || '').trim()}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={savingMode !== null} className="btn-secondary" data-testid="agent-code-change-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={savingMode !== null} className="btn-primary" data-testid="agent-code-change-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> {savingMode !== null ? 'Saving...' : confirming.confirmCta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentCodeChange;
